import { useState, type FormEvent } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { useConfigQuery } from '@/context/ClubContext'
import {
  AsyncState,
  Button,
  ConfirmDialog,
  DaysPicker,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  PageHead
} from '@/components/ui'
import { ClubGate } from '@/components/ClubGate'
import { type Tariff, ALL_DAYS_MASK, describeDaysMask, describeTimeWindow } from '@/lib/config'
import { money } from '@/lib/format'
import { Cur } from '@/lib/currency'

export function Tariffs(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Конфигурация"
        title="Тарифы"
        lead="Тарифные планы клуба: цена за час, цветовая метка и опциональное расписание (часы и дни недели), когда тариф действует."
      />
      <ClubGate>
        <TariffsTable />
      </ClubGate>
    </>
  )
}

function TariffsTable(): React.JSX.Element {
  const query = useConfigQuery()
  const list = useFetch<Tariff[]>('/v1/tariffs', query, [query?.clubId])
  const [editing, setEditing] = useState<Tariff | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Tariff | null>(null)
  const del = useMutate()

  const rows = list.data ?? []

  const onDeleteConfirm = async (): Promise<void> => {
    if (!deleting) return
    const ok = await del.mutate(() => api.del(`/v1/tariffs/${deleting.id}`, { query }))
    if (ok) {
      setDeleting(null)
      list.reload()
    }
  }

  return (
    <>
      <div className="toolbar">
        <div className="toolbar__left">
          <span className="muted">{rows.length} тариф(ов)</span>
        </div>
        <div className="toolbar__right">
          <Button variant="primary" size="sm" onClick={() => setEditing('new')}>
            {Icon.plus}
            Новый тариф
          </Button>
        </div>
      </div>

      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Тарифы ещё не заданы. Создайте первый."
      />

      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Название</th>
                <th className="num">Цена / час</th>
                <th>Время</th>
                <th>Дни</th>
                <th style={{ width: 96 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td>
                    {t.color && <span className="swatch" style={{ background: t.color }} />}
                    {t.name}
                  </td>
                  <td className="num">
                    {money(t.pricePerHour)} <Cur />
                  </td>
                  <td>{describeTimeWindow(t.timeFrom, t.timeTo)}</td>
                  <td>{describeDaysMask(t.daysMask)}</td>
                  <td>
                    <div className="row-actions">
                      <IconButton title="Изменить" onClick={() => setEditing(t)}>
                        {Icon.edit}
                      </IconButton>
                      <IconButton danger title="Удалить" onClick={() => setDeleting(t)}>
                        {Icon.trash}
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TariffForm
          tariff={editing === 'new' ? null : editing}
          query={query}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            list.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Удалить тариф"
          message={`Удалить тариф «${deleting.name}»? Действие нельзя отменить.`}
          busy={del.saving}
          error={del.error}
          onConfirm={() => void onDeleteConfirm()}
          onCancel={() => setDeleting(null)}
        />
      )}
    </>
  )
}

function TariffForm({
  tariff,
  query,
  onClose,
  onSaved
}: {
  tariff: Tariff | null
  query: ReturnType<typeof useConfigQuery>
  onClose: () => void
  onSaved: () => void
}): React.JSX.Element {
  const isEdit = tariff !== null
  const [name, setName] = useState(tariff?.name ?? '')
  const [price, setPrice] = useState(tariff ? String(tariff.pricePerHour) : '')
  const [color, setColor] = useState(tariff?.color ?? '#c9a84c')
  const [scheduled, setScheduled] = useState(
    Boolean(tariff?.timeFrom || tariff?.timeTo || tariff?.daysMask)
  )
  const [timeFrom, setTimeFrom] = useState(tariff?.timeFrom ?? '')
  const [timeTo, setTimeTo] = useState(tariff?.timeTo ?? '')
  const [daysMask, setDaysMask] = useState(tariff?.daysMask ?? ALL_DAYS_MASK)
  const [validation, setValidation] = useState<string | null>(null)
  const save = useMutate()

  const onSubmit = async (e: FormEvent): Promise<void> => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return setValidation('Укажите название тарифа.')
    const priceNum = Number(price)
    if (!Number.isFinite(priceNum) || priceNum < 0)
      return setValidation('Цена должна быть неотрицательным числом.')
    setValidation(null)

    const body = {
      name: trimmed,
      pricePerHour: priceNum,
      color: color || null,
      timeFrom: scheduled ? timeFrom || null : null,
      timeTo: scheduled ? timeTo || null : null,
      daysMask: scheduled ? daysMask : null
    }

    const ok = await save.mutate(() =>
      isEdit
        ? api.patch(`/v1/tariffs/${tariff.id}`, body, { query })
        : api.post('/v1/tariffs', body, { query })
    )
    if (ok) onSaved()
  }

  return (
    <Modal
      title={isEdit ? 'Изменить тариф' : 'Новый тариф'}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={save.saving}>
            Отмена
          </Button>
          <Button variant="primary" busy={save.saving} type="submit" form="tariff-form">
            Сохранить
          </Button>
        </>
      }
    >
      <form id="tariff-form" onSubmit={(e) => void onSubmit(e)}>
        <div className="form-grid">
          <Field label="Название" htmlFor="t-name" full>
            <Input
              id="t-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="напр. Дневной"
              autoFocus
            />
          </Field>
          <Field
            label={
              <>
                Цена за час, <Cur />
              </>
            }
            htmlFor="t-price"
          >
            <Input
              id="t-price"
              type="number"
              min={0}
              step="any"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0"
            />
          </Field>
          <Field label="Цвет" htmlFor="t-color">
            <Input
              id="t-color"
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </Field>

          <div className="field field--full">
            <label className="checkbox">
              <input
                type="checkbox"
                checked={scheduled}
                onChange={(e) => setScheduled(e.target.checked)}
              />
              Действует по расписанию (иначе — всегда)
            </label>
          </div>

          {scheduled && (
            <>
              <Field label="С (ЧЧ:ММ)" htmlFor="t-from">
                <Input
                  id="t-from"
                  type="time"
                  value={timeFrom}
                  onChange={(e) => setTimeFrom(e.target.value)}
                />
              </Field>
              <Field label="По (ЧЧ:ММ)" htmlFor="t-to">
                <Input
                  id="t-to"
                  type="time"
                  value={timeTo}
                  onChange={(e) => setTimeTo(e.target.value)}
                />
              </Field>
              <Field label="Дни недели" full>
                <DaysPicker mask={daysMask} onChange={setDaysMask} />
              </Field>
            </>
          )}
        </div>

        {validation && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {validation}
          </div>
        )}
        {save.error && (
          <div className="form-error" style={{ marginTop: 14 }}>
            {save.error}
          </div>
        )}
      </form>
    </Modal>
  )
}
